import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useSession } from '../../lib/auth.js';
import FilterBuilder, { ensureGroup } from '../../components/FilterBuilder.jsx';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export default function Watchlists() {
  const { workspaces } = useSession();
  const ws = workspaces && workspaces[0];
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/v1/watchlists?workspace_id=${ws.id}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setWatchlists(data.watchlists || []);
      }
    } catch (_) { /* swallow */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [ws?.id]);

  const handleDelete = async (id) => {
    if (!confirm('Delete this watchlist? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await fetch(`${API_BASE}/v1/watchlists/${id}`, { method: 'DELETE', credentials: 'include' });
      setWatchlists((prev) => prev.filter((w) => w.id !== id));
    } catch (_) { /* swallow */ }
    setDeleting(null);
  };

  return (
    <div>
      <div style={headerRow}>
        <h1 style={title}>Watchlists</h1>
        <button onClick={() => setShowCreate(true)} style={primaryBtn}>
          <Plus size={14} /> New Watchlist
        </button>
      </div>

      {loading ? (
        <div style={center}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : watchlists.length === 0 ? (
        <p style={empty}>No watchlists yet. Create one to start matching notices.</p>
      ) : (
        <div style={list}>
          {watchlists.map((wl) => (
            <div key={wl.id} style={card}>
              <div style={cardMain}>
                <span style={cardName}>{wl.name}</span>
                <span style={cardMeta}>
                  {wl.enabled ? 'Enabled' : 'Disabled'}  -  {new Date(wl.created_at).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={() => handleDelete(wl.id)}
                disabled={deleting === wl.id}
                style={deleteBtn}
                title="Delete watchlist"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateModal
          workspaceId={ws?.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateModal({ workspaceId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('builder');
  const [filter, setFilter] = useState(ensureGroup({ field: 'country', op: 'eq', value: 'CH' }));
  const [filterJson, setFilterJson] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const switchMode = (m) => {
    if (m === 'json') {
      setFilterJson(JSON.stringify(filter, null, 2));
    } else {
      try {
        setFilter(ensureGroup(JSON.parse(filterJson)));
      } catch (_) { /* keep current builder state */ }
    }
    setMode(m);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let f = filter;
    if (mode === 'json') {
      try {
        f = JSON.parse(filterJson);
      } catch (err) {
        setError('Invalid JSON: ' + err.message);
        return;
      }
    }

    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/v1/watchlists`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, name: name.trim(), filter: f }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.error?.message || `HTTP ${r.status}`);
        setSaving(false);
        return;
      }
      onCreated();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, width: mode === 'builder' ? 720 : 480 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalTitle}>New Watchlist</h2>
        <form onSubmit={handleSubmit}>
          <label style={label}>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Swiss IT tenders"
              required
              style={input}
            />
          </label>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={tabRow}>
              <button type="button" onClick={() => switchMode('builder')} style={mode === 'builder' ? tabActive : tab}>Builder</button>
              <button type="button" onClick={() => switchMode('json')} style={mode === 'json' ? tabActive : tab}>JSON</button>
            </div>
            {mode === 'builder' ? (
              <FilterBuilder value={filter} onChange={setFilter} />
            ) : (
              <textarea
                value={filterJson}
                onChange={(e) => setFilterJson(e.target.value)}
                rows={10}
                style={{ ...input, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-12)', resize: 'vertical' }}
              />
            )}
          </div>
          {error && <p style={errText}>{error}</p>}
          <div style={btnRow}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={primaryBtn}>
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const headerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-5)' };
const title = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)', margin: 0 };
const center = { textAlign: 'center', padding: 'var(--space-8)', color: 'var(--fg-2)' };
const empty = { color: 'var(--fg-3)', fontSize: 'var(--fs-14)' };
const list = { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' };
const card = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', background: 'var(--bg-2)', borderRadius: 'var(--r-2)', border: '1px solid var(--line-1)' };
const cardMain = { display: 'flex', flexDirection: 'column', gap: 2 };
const cardName = { fontSize: 'var(--fs-14)', fontWeight: 500, color: 'var(--fg-0)' };
const cardMeta = { fontSize: 'var(--fs-12)', color: 'var(--fg-3)' };
const deleteBtn = { background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 'var(--space-2)', borderRadius: 'var(--r-1)' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-4)', background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-13)', fontWeight: 500, cursor: 'pointer' };
const secondaryBtn = { padding: 'var(--space-2) var(--space-4)', background: 'var(--bg-3)', color: 'var(--fg-2)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-13)', cursor: 'pointer' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modal = { background: 'var(--bg-2)', borderRadius: 'var(--r-3)', padding: 'var(--space-5)', width: 480, maxWidth: '90vw', border: '1px solid var(--line-2)' };
const modalTitle = { fontSize: 'var(--fs-18)', fontWeight: 600, color: 'var(--fg-0)', marginBottom: 'var(--space-4)', marginTop: 0 };
const label = { display: 'block', fontSize: 'var(--fs-13)', color: 'var(--fg-2)', marginBottom: 'var(--space-3)' };
const input = { display: 'block', width: '100%', marginTop: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)', background: 'var(--bg-3)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-1)', color: 'var(--fg-0)', fontSize: 'var(--fs-14)', boxSizing: 'border-box' };
const errText = { color: 'var(--danger)', fontSize: 'var(--fs-12)', marginTop: 'var(--space-2)' };
const btnRow = { display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', marginTop: 'var(--space-4)' };
const tabRow = { display: 'flex', gap: 'var(--space-1)', marginBottom: 'var(--space-2)' };
const tab = { padding: '4px 12px', fontSize: 'var(--fs-12)', background: 'none', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', color: 'var(--fg-3)', cursor: 'pointer' };
const tabActive = { ...tab, background: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'var(--accent)' };
